@if (isLoading()) {
  <div class="pf-loading">
    <mat-spinner diameter="40" />
  </div>
} @else {
  <form [formGroup]="form" (ngSubmit)="onSave()" class="pf-root">
    <div class="pf-save-bar">
      <div class="pf-save-bar__inner">
        <div class="pf-save-bar__left">
          <button
            type="button"
            class="pf-back-btn"
            (click)="onCancel()"
            [attr.aria-label]="'projectForm.back' | translate"
          >
            <mat-icon fontSet="material-icons-outlined" aria-hidden="true">chevron_left</mat-icon>
          </button>
          <div>
            <div class="pf-save-bar__eyebrow">
              {{ (isEditMode() ? 'projectForm.eyebrowEdit' : 'projectForm.eyebrowCreate') | translate }}
            </div>
            <div class="pf-save-bar__title">
              {{ (isEditMode() ? 'projectForm.titleEdit' : 'projectForm.titleCreate') | translate }}
            </div>
          </div>
          @if (isDirty()) {
            <span class="pf-dirty-pill">
              <span class="pf-dirty-pill__dot" aria-hidden="true"></span>
              {{ 'projectForm.unsavedChanges' | translate }}
            </span>
          }
        </div>
        <div class="pf-save-bar__actions">
          <button mat-stroked-button type="button" (click)="onCancel()">
            {{ 'projectForm.cancel' | translate }}
          </button>
          <button mat-flat-button color="primary" type="submit" [disabled]="saveDisabled()">
            @if (isSaving()) {
              <mat-spinner diameter="18" />
            } @else {
              <mat-icon aria-hidden="true">check</mat-icon>
            }
            {{ (isEditMode() ? 'projectForm.saveEdit' : 'projectForm.save') | translate }}
          </button>
        </div>
      </div>
    </div>

    <div class="pf-content">
      <div class="pf-preview">
        <div
          class="pf-preview__icon"
          [style.color]="previewIcon().color"
          [style.border-color]="'color-mix(in srgb, ' + previewIcon().color + ' 19%, transparent)'"
        >
          <mat-icon fontSet="material-icons-outlined" aria-hidden="true">{{ previewIcon().matIcon }}</mat-icon>
        </div>
        <div class="pf-preview__info">
          <div class="pf-preview__eyebrow">{{ 'projectForm.preview' | translate }}</div>
          <div class="pf-preview__name" [class.pf-preview__name--placeholder]="!previewName()">
            {{ previewName() || ('projectForm.fields.namePlaceholder' | translate) }}
          </div>
          <div class="pf-preview__meta">
            @if (previewResponsibleName()) {
              <span class="pf-preview__responsible">
                <app-avatar [name]="previewResponsibleName()" [size]="22" />
                {{ previewResponsibleName() }}
              </span>
            }
            @if (previewStatus()) {
              @if (previewResponsibleName()) {
                <span class="pf-preview__dot" aria-hidden="true"></span>
              }
              <app-status-pill [kind]="previewStatusKind()" [label]="previewStatusLabel()" size="sm" />
            }
            @if (previewJiraKeys()) {
              @if (previewResponsibleName() || previewStatus()) {
                <span class="pf-preview__dot" aria-hidden="true"></span>
              }
              <code class="pf-preview__jira">Jira: {{ previewJiraKeys() }}</code>
            }
          </div>
        </div>
      </div>

      <div class="pf-columns">
        <div class="pf-main">
          <section class="pf-card">
            <div class="pf-card__header">
              <div class="pf-card__title">{{ 'projectForm.sections.grunddaten' | translate }}</div>
              <div class="pf-card__subtitle">{{ 'projectForm.sections.grunddatenSubtitle' | translate }}</div>
            </div>
            <div class="pf-card__body pf-stack">
              <mat-form-field appearance="outline" floatLabel="always" class="pf-field">
                <mat-label>{{ 'projectForm.fields.name' | translate }}</mat-label>
                <input
                  matInput
                  formControlName="name"
                  [attr.maxlength]="fieldLengths.NAME_MAX"
                  [placeholder]="'projectForm.fields.namePlaceholder' | translate"
                />
                @if (form.controls.name.hasError('required') && form.controls.name.touched) {
                  <mat-error>{{ 'projectForm.errors.required' | translate }}</mat-error>
                }
              </mat-form-field>
              <mat-form-field appearance="outline" floatLabel="always" class="pf-field">
                <mat-label>{{ 'projectForm.fields.description' | translate }}</mat-label>
                <textarea
                  matInput
                  formControlName="description"
                  rows="4"
                  [attr.maxlength]="fieldLengths.FREETEXT_MAX"
                  [placeholder]="'projectForm.fields.descriptionPlaceholder' | translate"
                ></textarea>
                <mat-hint align="end" aria-live="polite">
                  {{ form.controls.description.value.length }} / {{ fieldLengths.FREETEXT_MAX }}
                </mat-hint>
              </mat-form-field>
              <div>
                <div class="pf-field-label">{{ 'projectForm.fields.icon' | translate }}</div>
                <div class="pf-icon-picker">
                  @for (ic of icons; track ic.name) {
                    <button
                      type="button"
                      class="pf-icon-btn"
                      [class.pf-icon-btn--active]="form.controls.icon.value === ic.name"
                      [style.--icon-color]="ic.color"
                      (click)="selectIcon(ic.name)"
                      [attr.aria-pressed]="form.controls.icon.value === ic.name"
                      [attr.aria-label]="ic.labelKey | translate"
                    >
                      <mat-icon fontSet="material-icons-outlined" aria-hidden="true">{{ ic.matIcon }}</mat-icon>
                    </button>
                  }
                </div>
              </div>
            </div>
          </section>

          <section class="pf-card">
            <div class="pf-card__header">
              <div class="pf-card__title">{{ 'projectForm.sections.zeitraum' | translate }}</div>
              <div class="pf-card__subtitle">{{ 'projectForm.sections.zeitraumSubtitle' | translate }}</div>
            </div>
            <div class="pf-card__body">
              <div class="pf-date-row">
                <mat-form-field appearance="outline" floatLabel="always" class="pf-field">
                  <mat-label>{{ 'projectForm.fields.startDate' | translate }}</mat-label>
                  <input
                    matInput
                    [matDatepicker]="startPicker"
                    formControlName="startDate"
                    data-testid="project-start-date"
                    [attr.aria-label]="'projectForm.fields.startDate' | translate"
                  />
                  <mat-datepicker-toggle matIconSuffix [for]="startPicker" />
                  <mat-datepicker #startPicker />
                  @if (form.controls.startDate.hasError('matDatepickerParse')) {
                    <mat-error>{{ 'common.errors.invalidDate' | translate }}</mat-error>
                  } @else if (form.controls.startDate.hasError('required') && form.controls.startDate.touched) {
                    <mat-error>{{ 'projectForm.errors.required' | translate }}</mat-error>
                  }
                </mat-form-field>
                <mat-form-field appearance="outline" floatLabel="always" class="pf-field">
                  <mat-label>{{ 'projectForm.fields.endDate' | translate }}</mat-label>
                  <input
                    matInput
                    [matDatepicker]="endPicker"
                    formControlName="endDate"
                    data-testid="project-end-date"
                    [attr.aria-label]="'projectForm.fields.endDate' | translate"
                  />
                  <mat-datepicker-toggle matIconSuffix [for]="endPicker" />
                  <mat-datepicker #endPicker />
                  @if (form.controls.endDate.hasError('matDatepickerParse')) {
                    <mat-error>{{ 'common.errors.invalidDate' | translate }}</mat-error>
                  }
                </mat-form-field>
              </div>
              @if (durationDays() !== null && durationDays()! >= 0) {
                <div class="pf-duration">
                  <mat-icon fontSet="material-icons-outlined" aria-hidden="true">bar_chart</mat-icon>
                  {{ 'projectForm.duration' | translate }}
                  <strong>{{ durationDays() }} {{ 'projectForm.days' | translate }}</strong>
                  &nbsp;·&nbsp;
                  {{ form.controls.startDate.value | date: 'dd. MMM' }}
                  –
                  {{ form.controls.endDate.value | date: 'dd. MMM yyyy' }}
                </div>
              }
            </div>
          </section>

          <section class="pf-card">
            @if (teamAlertKey(); as alertKey) {
              <div class="pf-card__alert" role="alert" aria-live="polite">
                <mat-icon aria-hidden="true">error_outline</mat-icon>
                <span>{{ alertKey | translate }}</span>
              </div>
            }
            <div class="pf-card__header pf-card__header--row">
              <div class="pf-card__title">
                {{ 'projectForm.sections.team' | translate }}
                <span class="pf-card__badge">{{ team().length }}</span>
              </div>
              @if (previewResponsibleName()) {
                <span class="pf-card__responsible">
                  {{ previewResponsibleLabelKey() | translate }}:
                  <strong>{{ previewResponsibleName() }}</strong>
                </span>
              } @else {
                <span class="pf-card__responsible pf-card__responsible--missing">
                  {{ 'projectForm.team.noLead' | translate }}
                </span>
              }
            </div>
            <div class="pf-card__body pf-stack">
              @for (member of team(); track member.userId) {
                <div class="pf-member">
                  <app-avatar [name]="member.name" [size]="32" />
                  <div class="pf-member__info">
                    <div class="pf-member__name">{{ member.name }}</div>
                  </div>
                  <select
                    class="pf-role-select"
                    [class.pf-role-select--unset]="member.role === null"
                    [ngModel]="member.role ?? ''"
                    [ngModelOptions]="{ standalone: true }"
                    (ngModelChange)="updateTeamRole(member.userId, $any($event) || null)"
                    [attr.aria-label]="('projectForm.team.roleFor' | translate) + ': ' + member.name"
                  >
                    <option value="" disabled>{{ 'projectForm.team.roles.choose' | translate }}</option>
                    @for (r of teamRoles; track r.value) {
                      <option [value]="r.value">{{ r.labelKey | translate }}</option>
                    }
                  </select>
                  <button
                    type="button"
                    class="pf-member__remove"
                    (click)="removeTeamMember(member.userId)"
                    [attr.aria-label]="'projectForm.team.remove' | translate"
                  >
                    <mat-icon fontSet="material-icons-outlined" aria-hidden="true">close</mat-icon>
                  </button>
                </div>
              }

              <div class="pf-member-add" (focusin)="openMemberSearch()" (focusout)="onMemberAddFocusOut($event)">
                <div class="pf-member-search">
                  <mat-icon class="pf-member-search__icon" fontSet="material-icons-outlined" aria-hidden="true">
                    add
                  </mat-icon>
                  <input
                    #memberSearchInput
                    type="search"
                    class="pf-member-search__input"
                    [placeholder]="'projectForm.team.addPlaceholder' | translate"
                    [attr.aria-label]="'projectForm.team.addPlaceholder' | translate"
                    [value]="memberSearch()"
                    (input)="memberSearch.set(memberSearchInput.value)"
                  />
                </div>
                @if (memberSearchOpen() && filteredUsers().length > 0) {
                  <div class="pf-user-dropdown">
                    @for (user of filteredUsers(); track user.id) {
                      <button type="button" class="pf-user-option" (click)="addTeamMember(user)">
                        <app-avatar [name]="user.displayName" [size]="28" />
                        <span>{{ user.displayName }}</span>
                        <mat-icon class="pf-user-option__add" fontSet="material-icons-outlined" aria-hidden="true">
                          add
                        </mat-icon>
                      </button>
                    }
                  </div>
                } @else if (memberSearchOpen()) {
                  <div class="pf-user-dropdown pf-user-dropdown--empty">
                    {{ 'projectForm.team.noMatches' | translate }}
                  </div>
                }
              </div>
            </div>
          </section>

          @if (kiSichtbar()) {
            <section class="pf-card" data-testid="ki-verbindung-abschnitt">
              <div class="pf-card__header">
                <div class="pf-card__title">{{ 'projectForm.sections.kiVerbindung' | translate }}</div>
                <div class="pf-card__subtitle">{{ 'projectForm.sections.kiVerbindungSubtitle' | translate }}</div>
              </div>
              <div class="pf-card__body">
                @if (projectId(); as id) {
                  <app-ki-verbindung-card
                    [projectId]="id"
                    [nurLesen]="!kiDarfBearbeiten()"
                    (zustandGemeldet)="kiZustand.set($event)"
                  />
                } @else {
                  <p class="pf-ki-hinweis" data-testid="ki-verbindung-nach-anlegen">
                    {{ 'projectForm.sections.kiVerbindungNachAnlegen' | translate }}
                  </p>
                }
              </div>
            </section>
          }

          <section class="pf-card">
            <div class="pf-card__header">
              <div class="pf-card__title">{{ 'projectForm.sections.integrationen' | translate }}</div>
              <div class="pf-card__subtitle">{{ 'projectForm.sections.integrationenSubtitle' | translate }}</div>
            </div>
            <div class="pf-card__body pf-stack">
              <div class="pf-connections">
                <div class="pf-connections__head">
                  <div class="pf-connections__icon" aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                    </svg>
                  </div>
                  <div class="pf-connections__info">
                    <div class="pf-connections__title">{{ 'projectForm.integration.trackerTitle' | translate }}</div>
                    <div class="pf-connections__subline">
                      {{ 'projectForm.integration.trackerSubline' | translate }}
                    </div>
                  </div>
                  <span class="pf-connections__counter">
                    {{ connections().length }}
                    {{
                      connections().length === 1
                        ? ('projectForm.integration.connection' | translate)
                        : ('projectForm.integration.connections' | translate)
                    }}
                  </span>
                </div>

                <div class="pf-connections__list">
                  @for (connection of connections(); track $index; let rowIndex = $index) {
                    <div>
                      <div class="pf-connection-row" [class.pf-connection-row--error]="!!connection.syncError">
                        <div
                          class="pf-tracker-toggle"
                          role="group"
                          data-testid="integration-tracker-toggle"
                          [attr.aria-label]="'projectForm.integration.trackerToggle' | translate"
                        >
                          @for (option of trackerOptions; track option) {
                            <button
                              type="button"
                              class="pf-tracker-toggle__segment"
                              [class.pf-tracker-toggle__segment--active]="connection.provider === option"
                              [style.color]="connection.provider === option ? providerColor(option) : null"
                              [attr.aria-pressed]="connection.provider === option"
                              (click)="setConnectionProvider(rowIndex, option)"
                            >
                              <span
                                class="pf-tracker-toggle__dot"
                                [style.background]="providerColor(option)"
                                aria-hidden="true"
                              ></span>
                              {{ providerLabel(option) }}
                            </button>
                          }
                        </div>
                        <div class="pf-board-field">
                          <svg
                            class="pf-board-field__icon"
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            aria-hidden="true"
                          >
                            <path
                              d="M9 15l6-6M10 6l1-1a4 4 0 015.7 5.7l-1 1M14 18l-1 1a4 4 0 01-5.7-5.7l1-1"
                              stroke="currentColor"
                              stroke-width="1.7"
                              stroke-linecap="round"
                            />
                          </svg>
                          <input
                            #connectionUrl
                            class="pf-board-field__input"
                            type="url"
                            data-testid="integration-url-input"
                            [id]="'connection-url-' + rowIndex"
                            [name]="'connection-url-' + rowIndex"
                            [value]="connection.url"
                            maxlength="500"
                            [placeholder]="connectionUrlPlaceholder(connection.provider)"
                            [attr.aria-label]="'projectForm.integration.urlLabel' | translate"
                            [attr.aria-invalid]="connection.syncError ? true : null"
                            [attr.aria-describedby]="connection.syncError ? 'connection-error-' + rowIndex : null"
                            (input)="updateConnectionUrl(rowIndex, connectionUrl.value)"
                          />
                        </div>
                        <div class="pf-board-field pf-board-field--key">
                          <label class="pf-board-field__key-label" [for]="'connection-key-' + rowIndex">
                            {{ connectionKeyLabelKey(connection.provider) | translate }}
                          </label>
                          <input
                            #connectionKey
                            class="pf-board-field__key-input"
                            [style.color]="providerColor(connection.provider)"
                            type="text"
                            data-testid="integration-key-input"
                            [id]="'connection-key-' + rowIndex"
                            [name]="'connection-key-' + rowIndex"
                            [value]="connection.key"
                            [placeholder]="connectionKeyPlaceholder(connection.provider)"
                            [attr.maxlength]="connectionKeyMax(connection.provider)"
                            [attr.aria-describedby]="'connection-key-hint-' + rowIndex"
                            [attr.title]="
                              connection.provider === 'REDMINE'
                                ? ('projectForm.integration.keyTooltip.redmine' | translate)
                                : null
                            "
                            (input)="updateConnectionKey(rowIndex, connectionKey.value)"
                          />
                          <span [id]="'connection-key-hint-' + rowIndex" class="pf-board-field__hint">
                            {{
                              connection.provider === 'REDMINE'
                                ? ('projectForm.integration.keyTooltip.redmine' | translate)
                                : ('projectForm.jiraKeyAutoFillHint' | translate)
                            }}
                          </span>
                        </div>
                        <button
                          type="button"
                          class="pf-board-remove"
                          (click)="removeConnection(rowIndex)"
                          [attr.aria-label]="'projectForm.integration.removeConnection' | translate"
                          title="{{ 'projectForm.integration.removeConnection' | translate }}"
                        >
                          <mat-icon fontSet="material-icons-outlined" aria-hidden="true">delete_outline</mat-icon>
                        </button>
                      </div>
                      @if (connection.url && (connection.provider === 'REDMINE' || connection.key)) {
                        <div class="pf-board-preview">
                          <span class="pf-provider-badge" [style.background]="providerColor(connection.provider)">
                            {{ providerLabel(connection.provider) }}
                          </span>
                          @if (connection.provider === 'REDMINE' && connection.key) {
                            <span class="pf-alias-badge">{{ connection.key }}</span>
                          }
                          <code class="pf-board-preview__link">{{ connectionPreviewUrl(connection) }}</code>
                          @if (connection.provider === 'REDMINE') {
                            <span class="pf-board-preview__hint" data-testid="integration-alias-hint">
                              {{ 'projectForm.integration.aliasHint.redmine' | translate }}
                            </span>
                          }
                        </div>
                      }
                      @if (connection.syncError) {
                        <div
                          class="pf-row-error"
                          role="status"
                          [id]="'connection-error-' + rowIndex"
                          data-testid="integration-row-error"
                        >
                          {{ connection.syncError.key | translate: connection.syncError.params }}
                        </div>
                      }
                    </div>
                  }
                  @if (connections().length < maxConnections) {
                    <button
                      type="button"
                      class="pf-add-board-btn"
                      data-testid="integration-add-button"
                      (click)="addConnection()"
                    >
                      <mat-icon fontSet="material-icons-outlined" aria-hidden="true">add</mat-icon>
                      {{ 'projectForm.integration.addConnection' | translate }}
                    </button>
                  }
                </div>
              </div>

              <div class="pf-integration">
                <div class="pf-integration__icon pf-integration__icon--github">
                  <mat-icon fontSet="material-icons-outlined" aria-hidden="true">code</mat-icon>
                </div>
                <div class="pf-integration__info">
                  <div class="pf-integration__name">{{ 'projectForm.githubTitle' | translate }}</div>
                  <div class="pf-integration__desc">{{ 'projectForm.githubDesc' | translate }}</div>
                </div>
                <span class="pf-integration__counter">
                  {{ githubRepos().length }}
                  {{
                    githubRepos().length === 1 ? ('projectForm.repo' | translate) : ('projectForm.repos' | translate)
                  }}
                </span>
              </div>

              <div class="pf-board-list">
                @for (repo of githubRepos(); track $index) {
                  <div>
                    <div class="pf-board-row pf-board-row--url-only" [class.pf-board-row--error]="!!repo.syncError">
                      <div class="pf-board-field">
                        <mat-icon class="pf-board-field__icon" fontSet="material-icons-outlined" aria-hidden="true">
                          link
                        </mat-icon>
                        <input
                          #githubRepoUrl
                          class="pf-board-field__input"
                          type="url"
                          [id]="'github-repo-url-' + $index"
                          [name]="'github-repo-url-' + $index"
                          [value]="repo.url"
                          maxlength="500"
                          placeholder="https://github.com/SpiritTesting/testOFFICE-ng-agentic"
                          [attr.aria-label]="'projectForm.fields.githubRepoUrl' | translate"
                          [attr.aria-invalid]="repo.syncError ? true : null"
                          [attr.aria-describedby]="repo.syncError ? 'github-repo-error-' + $index : null"
                          (input)="updateGithubRepoUrl($index, githubRepoUrl.value)"
                        />
                      </div>
                      <button
                        type="button"
                        class="pf-board-remove"
                        (click)="removeGithubRepo($index)"
                        [attr.aria-label]="'projectForm.integration.removeRepo' | translate"
                        title="{{ 'projectForm.integration.removeRepo' | translate }}"
                      >
                        <mat-icon aria-hidden="true">delete_outline</mat-icon>
                      </button>
                    </div>
                    @if (repo.syncError) {
                      <div
                        class="pf-row-error"
                        role="status"
                        [id]="'github-repo-error-' + $index"
                        data-testid="integration-row-error"
                      >
                        {{ repo.syncError.key | translate: repo.syncError.params }}
                      </div>
                    }
                  </div>
                }
                @if (githubRepos().length < maxConnections) {
                  <button type="button" class="pf-add-board-btn" (click)="addGithubRepo()">
                    <mat-icon fontSet="material-icons-outlined" aria-hidden="true">add</mat-icon>
                    {{ 'projectForm.integration.addRepo' | translate }}
                  </button>
                }
              </div>

              @if (isEditMode() && projectId() && canManageRequirementTypes()) {
                <app-typ-sync-card
                  [projectId]="projectId()!"
                  [integrations]="existingIntegrations()"
                  [requirementTypes]="requirementTypes()"
                  (typesChanged)="onRequirementTypesChanged()"
                />
              }
            </div>
          </section>
        </div>

        <aside class="pf-sidebar">
          <section class="pf-card">
            <div class="pf-sidebar-section-title">{{ 'projectForm.sidebar.properties' | translate }}</div>
            <div class="pf-stack pf-stack--sm">
              <mat-form-field appearance="outline" floatLabel="always" class="pf-field">
                <mat-label>{{ 'projectForm.fields.status' | translate }}</mat-label>
                <mat-select formControlName="status">
                  @for (opt of statusOptions; track opt.value) {
                    <mat-option [value]="opt.value">{{ opt.labelKey | translate }}</mat-option>
                  }
                </mat-select>
                @if (form.controls.status.hasError('required') && form.controls.status.touched) {
                  <mat-error>{{ 'projectForm.errors.required' | translate }}</mat-error>
                }
              </mat-form-field>
              <mat-form-field appearance="outline" floatLabel="always" class="pf-field">
                <mat-label>{{ 'projectForm.fields.tags' | translate }}</mat-label>
                <input
                  matInput
                  formControlName="tags"
                  [attr.maxlength]="fieldLengths.FREETEXT_MAX"
                  [placeholder]="'projectForm.fields.tagsPlaceholder' | translate"
                />
              </mat-form-field>
            </div>
          </section>
        </aside>
      </div>
    </div>
  </form>
}

<lib-modal
  [title]="'projectForm.deactivateConfirm.title' | translate"
  [text]="'projectForm.deactivateConfirm.message' | translate"
  [visible]="showDeactivateConfirm()"
  [saveText]="'projectForm.deactivateConfirm.confirm' | translate"
  [cancelText]="'projectForm.deactivateConfirm.cancel' | translate"
  (save)="onDeactivateConfirmed()"
  (cancel)="onDeactivateCancelled()"
  (visibleChange)="!$event && onDeactivateCancelled()"
></lib-modal>
